import { pickMimeType } from './mime'

const STORAGE_KEY = 'ozvuchka.recorderLag'

/**
 * Задержка старта MediaRecorder в секундах (положительная: рекордер начинает
 * писать позже, чем мы его попросили). См. docs/RECORDING.md, «Калибровка задержки».
 */
export function getRecorderLag(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return 0
    const value = Number(raw)
    if (!Number.isFinite(value)) return 0
    // Разумный диапазон: 0…500 мс. Всё остальное — мусор или неудачная калибровка.
    return Math.min(0.5, Math.max(0, value))
  } catch {
    return 0
  }
}

export function setRecorderLag(seconds: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(seconds))
  } catch {
    // localStorage может быть недоступен (приватный режим) — не критично.
  }
}

export function clearRecorderLag(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // см. выше
  }
}

export type CalibrationResult = {
  /** Задержка старта рекордера, сек. */
  lag: number
  /** Пиковая амплитуда щелчка (0..1) — по ней видно, услышал ли микрофон щелчок. */
  peak: number
}

const CLICK_AT = 0.6 // когда играем щелчок, сек после старта рекордера
const RECORD_FOR = 1.4 // сколько всего пишем, сек

function makeClick(ctx: AudioContext): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * 0.02)
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < length; i += 1) {
    // Короткий затухающий тон 2 кГц — узкий пик, легко ловится в записи.
    const t = i / ctx.sampleRate
    data[i] = Math.sin(2 * Math.PI * 2000 * t) * Math.exp(-t * 180)
  }
  return buffer
}

function findPeakTime(buffer: AudioBuffer): { time: number; peak: number } {
  const data = buffer.getChannelData(0)
  let peak = 0
  let peakIndex = 0
  for (let i = 0; i < data.length; i += 1) {
    const value = Math.abs(data[i] as number)
    if (value > peak) {
      peak = value
      peakIndex = i
    }
  }
  // Ищем не сам максимум, а первый переход через 40% от него — фронт щелчка.
  const threshold = peak * 0.4
  let onset = peakIndex
  for (let i = 0; i < data.length; i += 1) {
    if (Math.abs(data[i] as number) >= threshold) {
      onset = i
      break
    }
  }
  return { time: onset / buffer.sampleRate, peak }
}

/**
 * Прогон калибровки: одновременно стартуем запись и играем щелчок в динамики,
 * потом ищем пик в записанном буфере. НАУШНИКИ НУЖНО СНЯТЬ — щелчок должен
 * дойти до микрофона по воздуху.
 */
export async function runCalibration(stream: MediaStream): Promise<CalibrationResult> {
  const choice = pickMimeType()
  if (!choice) throw new Error('Браузер не поддерживает запись звука')

  const ctx = new AudioContext()
  const recorder =
    choice.mimeType === ''
      ? new MediaRecorder(stream)
      : new MediaRecorder(stream, { mimeType: choice.mimeType })
  const chunks: Blob[] = []
  recorder.ondataavailable = (e: BlobEvent): void => {
    if (e.data.size > 0) chunks.push(e.data)
  }

  const stopped = new Promise<void>((resolve, reject) => {
    recorder.onstop = (): void => resolve()
    recorder.onerror = (): void => reject(new Error('Ошибка записи при калибровке'))
  })
  const started = new Promise<void>((resolve) => {
    recorder.onstart = (): void => resolve()
  })

  try {
    if (ctx.state === 'suspended') await ctx.resume()
    recorder.start()
    await started

    const clickBuffer = makeClick(ctx)
    const source = ctx.createBufferSource()
    source.buffer = clickBuffer
    const gain = ctx.createGain()
    gain.gain.value = 0.9
    source.connect(gain).connect(ctx.destination)
    source.start(ctx.currentTime + CLICK_AT)

    await new Promise<void>((resolve) => window.setTimeout(resolve, RECORD_FOR * 1000))
    recorder.stop()
    await stopped

    const blob = new Blob(chunks, choice.mimeType ? { type: choice.mimeType } : undefined)
    const decoded = await ctx.decodeAudioData(await blob.arrayBuffer())
    const { time, peak } = findPeakTime(decoded)
    if (peak < 0.02) {
      throw new Error('Щелчок не слышен в записи. Снимите наушники и добавьте громкость.')
    }
    // Щелчок прозвучал в CLICK_AT после start(); в записи он оказался на time.
    // Если рекордер стартовал с опозданием lag, то time = CLICK_AT - lag.
    const lag = Math.min(0.5, Math.max(0, CLICK_AT - time))
    return { lag, peak }
  } finally {
    if (recorder.state !== 'inactive') recorder.stop()
    recorder.ondataavailable = null
    recorder.onstart = null
    recorder.onstop = null
    recorder.onerror = null
    await ctx.close().catch(() => undefined)
  }
}
